import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wrench, Mail, Phone, HardHat, Wallet, CheckCircle2, Clock, User, ChevronRight } from 'lucide-react';

const STATUS_STYLES = {
  active: { label: 'Attivo', variant: 'default' },
  pending: { label: 'In attesa', variant: 'secondary' },
  suspended: { label: 'Sospeso', variant: 'destructive' },
  inactive: { label: 'Disattivato', variant: 'outline' },
};

const fmtEuro = (n) => (Number(n || 0)).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};

export default function Installers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.adminListInstallers();
      setItems(Array.isArray(r) ? r : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento installatori');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter(u => {
      if (statusFilter !== 'all' && (u.status || 'active') !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q)
      );
    });
  }, [items, search, statusFilter]);

  // Statistiche aggregate globali
  const totals = useMemo(() => {
    return items.reduce((acc, u) => {
      const s = u.stats || {};
      acc.installers += 1;
      acc.active_interv += Number(s.active_interventions || 0);
      acc.completed_interv += Number(s.completed_interventions || 0);
      acc.compensation += Number(s.compensation_total || 0);
      return acc;
    }, { installers: 0, active_interv: 0, completed_interv: 0, compensation: 0 });
  }, [items]);

  return (
    <div>
      <PageHeader
        title="Installatori"
        description="Elenco tecnici installatori registrati. Da qui puoi vederne il carico di lavoro, i compensi maturati e accedere ai loro interventi."
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard icon={HardHat} label="Installatori" value={totals.installers} testid="kpi-installers-count" />
        <KpiCard icon={Clock} label="Interventi attivi" value={totals.active_interv} testid="kpi-active-interventions" />
        <KpiCard icon={CheckCircle2} label="Interventi completati" value={totals.completed_interv} testid="kpi-completed-interventions" />
        <KpiCard icon={Wallet} label="Compensi maturati" value={fmtEuro(totals.compensation)} testid="kpi-total-compensation" />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex gap-2 flex-1">
              <Input
                placeholder="Cerca per nome, email o telefono..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
                data-testid="installers-search-input"
              />
              <div className="flex gap-1">
                {[
                  { key: 'all', label: 'Tutti' },
                  { key: 'active', label: 'Attivi' },
                  { key: 'pending', label: 'In attesa' },
                  { key: 'suspended', label: 'Sospesi' },
                ].map(opt => (
                  <Button
                    key={opt.key}
                    variant={statusFilter === opt.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(opt.key)}
                    data-testid={`installers-filter-${opt.key}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="text-xs text-muted-foreground whitespace-nowrap">
              {filtered.length} installator{filtered.length === 1 ? 'e' : 'i'}
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3">
                <Wrench className="size-5 text-muted-foreground" />
              </div>
              <div className="text-sm font-medium">Nessun installatore trovato</div>
              <div className="text-xs text-muted-foreground mt-1">
                {items.length === 0
                  ? 'Crea un utente con ruolo "installer" dalla sezione Utenti.'
                  : 'Prova a modificare i filtri di ricerca.'}
              </div>
            </div>
          ) : (
            <Table data-testid="installers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Installatore</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-center">Interventi</TableHead>
                  <TableHead className="text-right">Compensi maturati</TableHead>
                  <TableHead>Ultimo intervento</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(u => {
                  const st = STATUS_STYLES[u.status || 'active'] || { label: u.status, variant: 'secondary' };
                  const s = u.stats || {};
                  return (
                    <TableRow
                      key={u.id}
                      data-testid={`installer-row-${u.id}`}
                      className="hover:bg-secondary/40 cursor-pointer"
                      onClick={() => navigate(`/installations?installer_id=${u.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="size-9 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                            <User className="size-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{u.full_name || '—'}</div>
                            <div className="text-[11px] text-muted-foreground font-mono">#{(u.id || '').slice(0, 8)}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Mail className="size-3 text-muted-foreground" />
                          <span className="truncate max-w-[220px]">{u.email}</span>
                        </div>
                        {u.phone && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                            <Phone className="size-3" />{u.phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                      <TableCell className="text-center">
                        <div className="inline-flex items-center gap-2 text-xs">
                          <span className="text-primary font-semibold" title="Attivi">{s.active_interventions || 0}</span>
                          <span className="text-muted-foreground">/</span>
                          <span className="text-muted-foreground" title="Totali">{s.total_interventions || 0}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground">attivi / totali</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmtEuro(s.compensation_total)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtDate(s.last_intervention_at)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/installations?installer_id=${u.id}`)}
                          data-testid={`installer-open-${u.id}`}
                        >
                          Interventi <ChevronRight className="size-3.5 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testid }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="size-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
          <Icon className="size-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-lg font-semibold truncate" data-testid={testid}>{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}
